import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getSquashSyncLogs, deleteSquashSyncLog } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../App';
import { ConfirmModal } from '../components/Modal';
import {
  HiOutlineRefresh, HiOutlineTrash, HiOutlineChevronDown, HiOutlineChevronRight,
  HiOutlineCheckCircle, HiOutlineExclamationCircle, HiOutlineExclamation,
} from 'react-icons/hi';
import { FiActivity } from 'react-icons/fi';

const STATUS_CONFIG = {
  success: { label: 'Succès',  className: 'badge bg-success',  icon: <HiOutlineCheckCircle /> },
  partial: { label: 'Partiel', className: 'badge bg-warning text-dark', icon: <HiOutlineExclamation /> },
  error:   { label: 'Erreur',  className: 'badge bg-danger',   icon: <HiOutlineExclamationCircle /> },
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export default function SquashTracking() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { isAdmin } = useAuth();
  const addToast = useToast();

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getSquashSyncLogs();
      setLogs(data);
    } catch {
      addToast('error', 'Erreur lors du chargement de l\'historique.');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteSquashSyncLog(confirmDelete);
      setConfirmDelete(null);
      setLogs((prev) => prev.filter((l) => l.id !== confirmDelete));
      addToast('success', 'Entrée supprimée.');
    } catch {
      addToast('error', 'Erreur lors de la suppression.');
    }
  };

  const filtered = logs.filter((l) => {
    const matchSearch =
      l.project_name.toLowerCase().includes(search.toLowerCase()) ||
      l.project_code.toLowerCase().includes(search.toLowerCase()) ||
      l.synced_by_username.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || l.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // Stats
  const total = logs.length;
  const successCount = logs.filter((l) => l.status === 'success').length;
  const partialCount = logs.filter((l) => l.status === 'partial').length;
  const errorCount  = logs.filter((l) => l.status === 'error').length;

  if (loading) return (
    <div className="loading-spinner">
      <output className="spinner-border"></output>
      <span>Chargement de l'historique...</span>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h2><FiActivity className="me-2" />Historique Sync Squash TM</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Trace de toutes les synchronisations vers la plateforme Squash TM
          </p>
        </div>
        <div className="actions">
          <button className="btn btn-outline-secondary btn-sm" onClick={loadLogs}>
            <HiOutlineRefresh /> Actualiser
          </button>
        </div>
      </div>
      {/* Stats cards */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <button type="button" className="stat-card" style={{ cursor: 'pointer', background: 'none', border: 'none', font: 'inherit', color: 'inherit', padding: 0, textAlign: 'left' }} onClick={() => setStatusFilter('all')}>
            <div className="stat-icon stat-icon-navy"><FiActivity /></div>
            <div className="stat-content">
              <div className="stat-number">{total}</div>
              <div className="stat-label">Total syncs</div>
            </div>
          </button>
        </div>
        <div className="col-6 col-md-3">
          <button type="button" className="stat-card" style={{ cursor: 'pointer', background: 'none', border: 'none', font: 'inherit', color: 'inherit', padding: 0, textAlign: 'left' }} onClick={() => setStatusFilter('success')}>
            <div className="stat-icon" style={{ background: '#d1fae5', color: '#065f46' }}>
              <HiOutlineCheckCircle />
            </div>
            <div className="stat-content">
              <div className="stat-number" style={{ color: '#065f46' }}>{successCount}</div>
              <div className="stat-label">Succès</div>
            </div>
          </button>
        </div>
        <div className="col-6 col-md-3">
          <button type="button" className="stat-card" style={{ cursor: 'pointer', background: 'none', border: 'none', font: 'inherit', color: 'inherit', padding: 0, textAlign: 'left' }} onClick={() => setStatusFilter('partial')}>
            <div className="stat-icon" style={{ background: '#fef3c7', color: '#92400e' }}>
              <HiOutlineExclamation />
            </div>
            <div className="stat-content">
              <div className="stat-number" style={{ color: '#92400e' }}>{partialCount}</div>
              <div className="stat-label">Partiels</div>
            </div>
          </button>
        </div>
        <div className="col-6 col-md-3">
          <button type="button" className="stat-card" style={{ cursor: 'pointer', background: 'none', border: 'none', font: 'inherit', color: 'inherit', padding: 0, textAlign: 'left' }} onClick={() => setStatusFilter('error')}>
            <div className="stat-icon" style={{ background: '#fee2e2', color: '#991b1b' }}>
              <HiOutlineExclamationCircle />
            </div>
            <div className="stat-content">
              <div className="stat-number" style={{ color: '#991b1b' }}>{errorCount}</div>
              <div className="stat-label">Erreurs</div>
            </div>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="d-flex gap-3 mb-3 flex-wrap align-items-center">
        <input
          className="form-control form-control-sm"
          placeholder="Rechercher par projet, code, utilisateur..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: '320px' }}
        />
        <select
          className="form-select form-select-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ maxWidth: '160px' }}
        >
          <option value="all">Tous les statuts</option>
          <option value="success">Succès</option>
          <option value="partial">Partiel</option>
          <option value="error">Erreur</option>
        </select>
        {statusFilter !== 'all' && (
          <button className="btn btn-sm btn-outline-secondary" onClick={() => setStatusFilter('all')}>
            Réinitialiser
          </button>
        )}
        <span className="text-muted ms-auto" style={{ fontSize: '0.8rem' }}>
          {filtered.length} résultat{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><FiActivity /></div>
          <p>{logs.length === 0 ? 'Aucune synchronisation effectuée.' : 'Aucun résultat pour ces filtres.'}</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ fontSize: '0.85rem' }}>
              <thead style={{ background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-200)' }}>
                <tr>
                  <th style={{ width: '28px' }}></th>
                  <th>Date</th>
                  <th>Projet</th>
                  <th>Synchronisé par</th>
                  <th>Statut</th>
                  <th className="text-center">Exigences</th>
                  <th className="text-center">Cas de test</th>
                  <th className="text-center">Couvertures</th>
                  <th className="text-center">Campagne</th>
                  {isAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry) => {
                  const statusCfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.error;
                  const isExpanded = expandedId === entry.id;
                  const hasErrors = entry.errors && entry.errors.length > 0;

                  return (
                    <React.Fragment key={entry.id}>
                      <tr
                        style={{ cursor: hasErrors ? 'pointer' : 'default', verticalAlign: 'middle' }}
                        onClick={() => hasErrors && setExpandedId(isExpanded ? null : entry.id)}
                        onKeyDown={(e) => { if (hasErrors && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setExpandedId(isExpanded ? null : entry.id); } }}
                      >
                        <td className="text-center text-muted">
                          {hasErrors
                            ? (isExpanded ? <HiOutlineChevronDown /> : <HiOutlineChevronRight />)
                            : null}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 500 }}>{formatDate(entry.synced_at)}</span>
                        </td>
                        <td>
                          {entry.project_id ? (
                            <Link
                              to={`/projects/${entry.project_id}`}
                              className="text-decoration-none fw-semibold text-navy"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {entry.project_name}
                            </Link>
                          ) : (
                            <span className="text-muted">{entry.project_name || 'Projet supprimé'}</span>
                          )}
                          <br />
                          <span className="project-code" style={{ fontSize: '0.75rem' }}>
                            {entry.project_code}
                          </span>
                        </td>
                        <td>{entry.synced_by_username || '—'}</td>
                        <td>
                          <span className={statusCfg.className} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            {statusCfg.icon} {statusCfg.label}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="badge bg-secondary">{entry.requirements_synced}</span>
                        </td>
                        <td className="text-center">
                          <span className="badge bg-secondary">{entry.test_cases_synced}</span>
                        </td>
                        <td className="text-center">
                          <span className="badge bg-secondary">{entry.coverages_created}</span>
                        </td>
                        <td className="text-center">
                          {entry.campaign_created
                            ? <span className="badge bg-success">Oui</span>
                            : <span className="badge bg-light text-muted">Non</span>}
                        </td>
                        {isAdmin && (
                          <td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              title="Supprimer cette entrée"
                              onClick={() => setConfirmDelete(entry.id)}
                            >
                              <HiOutlineTrash />
                            </button>
                          </td>
                        )}
                      </tr>

                      {/* Errors row */}
                      {isExpanded && hasErrors && (
                        <tr style={{ background: '#fff8f0' }}>
                          <td></td>
                          <td colSpan={isAdmin ? 8 : 7}>
                            <div style={{ padding: '8px 0' }}>
                              <strong style={{ color: '#92400e', fontSize: '0.8rem' }}>Erreurs rencontrées :</strong>
                              <ul className="mb-0 mt-1" style={{ fontSize: '0.8rem', color: '#7c3aed' }}>
                                {entry.errors.map((err, i) => (
                                  <li key={i}>{err}</li>
                                ))}
                              </ul>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <ConfirmModal
        show={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer l'entrée"
        message="Supprimer cette entrée de l'historique ? Cette action est irréversible."
        confirmText="Supprimer"
      />
    </div>
  );
}
